import { RegionId, UserId } from "@sp-crm/core";
import {
    useGetUsersForRegionQuery,
    useGetVisibleUsersAcrossRegionsQuery,
} from "generated/graphql";
import React, { useCallback, useMemo } from "react";
import Multiselect from "react-widgets/Multiselect";
import { stableQueryOptions } from "util/requests";

interface MultiUserSelectProps {
    onChange: (userIds: UserId[]) => void;
    value: UserId[];
    label: string;
    className?: string;
    regionId?: RegionId;
    placeholder?: string;
}

type UserObject = {
    key: UserId;
    text: string;
};

export const MultiUserSelect: React.FC<MultiUserSelectProps> = props => {
    const { onChange, value, label, className, regionId, placeholder } = props;

    const crossRegionUsersQuery = useGetVisibleUsersAcrossRegionsQuery(
        {},
        { ...stableQueryOptions(), enabled: !regionId },
    );
    const regionUsersQuery = useGetUsersForRegionQuery(
        { regionId: regionId! },
        { ...stableQueryOptions(), enabled: !!regionId },
    );

    const userSelectData = useMemo(() => {
        const users =
            regionUsersQuery.data?.getUsersForRegion ??
            crossRegionUsersQuery.data?.getVisibleUsersAcrossRegions;

        if (!users) {
            return null;
        }

        const availableUserIds = new Set(users.map(u => u.id));

        const values: UserObject[] = users
            .map(u => ({ key: u.id, text: u.preferredName }))
            .sort((a, b) => a.text.localeCompare(b.text));

        const filteredValue = value.filter(userId => availableUserIds.has(userId));

        return { values, filteredValue };
    }, [
        crossRegionUsersQuery.data?.getVisibleUsersAcrossRegions,
        regionUsersQuery.data?.getUsersForRegion,
        value,
    ]);

    const handleOnChange = useCallback(
        (selectedUsers: UserObject[]) => {
            const userIds = selectedUsers.map(x => x.key);
            onChange(userIds);
        },
        [onChange],
    );

    if (!userSelectData) {
        return null;
    }

    return (
        <div className={`${className ?? ""} w-full`}>
            <label>
                {label ? <div className="mb-1">{label}</div> : null}
                <Multiselect
                    data={userSelectData.values}
                    value={userSelectData.filteredValue}
                    dataKey="key"
                    textField="text"
                    placeholder={placeholder}
                    onChange={handleOnChange}
                />
            </label>
        </div>
    );
};
